import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
  galleryImageIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { ORDER_BY } from "../../utils/AppConstants";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { ICDropDown } from "../../core-component/form";
import {
  deleteGalleryDirectory,
  fetchAllGalleryDirectory,
  getGalleryDirectories,
  getGenderDdl,
  IGalleryDirectory,
} from "./GalleryDirectoryListController";
import GalleryDirectoryForm from "./GalleryDirectoryForm";
import ICImage from "../../core-component/ui/ICImage";
import { useNavigate } from "react-router-dom";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";

let timer: ReturnType<typeof setTimeout>;

const GalleryDirectoryListView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [advanceSearch, setAdvanceSearch] = useState<string>("");
  const [gender, setGender] = useState<IOption<string>[]>([]);
  const [galleryDirectoryList, setGalleryDirectoryList] = useState<
    IGalleryDirectory[]
  >([]);
  const [galleryDirectoryToEdit, setGalleryDirectoryToEdit] =
    useState<IGalleryDirectory>();
  const { pagination, setChangePagination } = UsePaginationHook();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMeasurementType({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchGender();
  }, []);

  const fetchMeasurementType = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await fetchAllGalleryDirectory(
        setGalleryDirectoryList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const fetchGender = async () => {
    await getGenderDdl(setGender);
  };

  const customActionView = (item: IGalleryDirectory) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setGalleryDirectoryToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setGalleryDirectoryToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setGalleryDirectoryToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("view_gallery_icon")}
          onClick={() => {
            navigate(
              "/" +
                routeMaster.admin +
                "/" +
                routeAdmin.galleryDirectoryItem +
                "/" +
                item.id,
            );
          }}
        >
          {galleryImageIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && galleryDirectoryToEdit) {
      const data = await deleteGalleryDirectory(galleryDirectoryToEdit.id);
      if (data) {
        fetchMeasurementType(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setGalleryDirectoryToEdit(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (galleryDirectoryToEdit) {
      setGalleryDirectoryToEdit(undefined);
    }
    if (status) {
      fetchMeasurementType(pagination);
    }
  };

  const filterView = (
    <div className="w-full grid grid-cols-4">
      <ICDropDown
        placeholder={t("gender")}
        selected={advanceSearch}
        options={gender}
        onSelect={(option) => setAdvanceSearch(option.data_value)}
      />
      <div className="!py-0 ml-4">
        <ICButton onClick={() => setAdvanceSearch("")} className="flex !w-fit">
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  const customFolderImage = (item: IGalleryDirectory) => (
    <div className="flex items-center">
      <ICImage
        imageUrl={item.folder_image ? item.folder_image : undefined}
        isThumbnail
        className="size-6 rounded-full"
        fallbackSrc={require("../../assets/image/Folder.jpg")}
        containerClass="!block !w-auto"
        noLoader
      />
      <div className="ml-4 w-1/2">{item.folder_name}</div>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchMeasurementType(pagination);
        }}
      />

      <ICTable<IGalleryDirectory>
        isLoading={isLoading}
        totalFlex="grid grid-cols-15"
        fields={getGalleryDirectories(customFolderImage, customActionView, t)}
        data={galleryDirectoryList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchMeasurementType({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchMeasurementType({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchMeasurementType({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <GalleryDirectoryForm
          handleClose={handleCloseForm}
          galleryDirectoryToEdit={galleryDirectoryToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && galleryDirectoryToEdit && (
        <ICConfirmationModal
          title={t("delete_gallery_directory")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", galleryDirectoryToEdit.folder_name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default GalleryDirectoryListView;
